// services/photoCache.js
// In-memory cache for photo blob URLs to prevent re-fetching on tab switches

// Map of photoId -> { blobUrl, timestamp }
const cache = new Map();

// Cache entry lifetime (matches presigned URL expiry - 12 hours)
const CACHE_LIFETIME_MS = 12 * 60 * 60 * 1000;

/**
 * Get a cached blob URL for a photo, fetching and caching if needed
 * @param {string} photoId - Unique photo identifier
 * @param {string} presignedUrl - S3 presigned URL to fetch from if not cached
 * @returns {Promise<string>} - Blob URL for the image
 */
export const getCachedPhotoUrl = async (photoId, presignedUrl) => {
  // Check if we have a valid cached entry
  const cached = cache.get(photoId);
  if (cached) {
    const age = Date.now() - cached.timestamp;
    if (age < CACHE_LIFETIME_MS) {
      return cached.blobUrl;
    }
    // Cache expired - revoke old blob URL and remove entry
    URL.revokeObjectURL(cached.blobUrl);
    cache.delete(photoId);
  }

  // Fetch the image and create a blob URL
  try {
    const response = await fetch(presignedUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status}`);
    }
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    // Store in cache
    cache.set(photoId, {
      blobUrl,
      timestamp: Date.now(),
    });

    return blobUrl;
  } catch (error) {
    console.error("Photo cache fetch error:", error);
    // Fall back to original URL if fetch fails
    return presignedUrl;
  }
};

/**
 * Check if a photo is already cached (synchronous)
 * @param {string} photoId - Unique photo identifier
 * @returns {string|null} - Cached blob URL or null
 */
export const getCachedPhotoUrlSync = (photoId) => {
  const cached = cache.get(photoId);
  if (cached) {
    const age = Date.now() - cached.timestamp;
    if (age < CACHE_LIFETIME_MS) {
      return cached.blobUrl;
    }
  }
  return null;
};

/**
 * Manually add a photo to the cache (useful for locally selected photos)
 * @param {string} photoId - Unique photo identifier
 * @param {string} blobUrl - Blob URL to cache
 */
export const setCachedPhotoUrl = (photoId, blobUrl) => {
  cache.set(photoId, {
    blobUrl,
    timestamp: Date.now(),
  });
};

/**
 * Remove a photo from the cache
 * @param {string} photoId - Unique photo identifier
 */
export const removeCachedPhoto = (photoId) => {
  const cached = cache.get(photoId);
  if (cached) {
    URL.revokeObjectURL(cached.blobUrl);
    cache.delete(photoId);
  }
};

/**
 * Clear all cached photos (useful on logout or form close)
 */
export const clearPhotoCache = () => {
  for (const entry of cache.values()) {
    URL.revokeObjectURL(entry.blobUrl);
  }
  cache.clear();
};

/**
 * Get cache statistics (for debugging)
 * @returns {object} - Cache stats
 */
export const getPhotoCacheStats = () => {
  return {
    size: cache.size,
    entries: Array.from(cache.keys()),
  };
};
